import * as React from 'react';
import { Slider as SliderPrimitive } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const QualitySlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    isLoading?: boolean;
    onValueChange?: (value: Array<number>) => void;
  }
>(({ className, isLoading, onValueChange, ...props }, ref) => {
  const tLabels = useTranslations('labels');
  const [inputValue, setInputValue] = React.useState<string>('');

  React.useEffect(() => {
    const currentValue = props.value?.[0] ?? 0;
    setInputValue(currentValue.toString());
  }, [props.value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue === '' || /^\d+$/.test(newValue)) {
      setInputValue(newValue);
    }
  };

  const handleInputBlur = () => {
    let numValue = parseInt(inputValue, 10);

    if (isNaN(numValue) || inputValue === '') {
      numValue = 0;
    }

    const min = props.min ?? 0;
    const max = props.max ?? 100;
    numValue = Math.max(min, Math.min(max, numValue));

    setInputValue(numValue.toString());
    onValueChange?.([numValue]);
  };

  return (
    <div className="flex items-center gap-2">
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none select-none items-center border border-ui-bevel bg-input-background p-px',
          className,
          props.disabled && 'cursor-not-allowed opacity-50',
        )}
        onValueChange={onValueChange}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-6 w-full overflow-hidden">
          <SliderPrimitive.Range
            className={cn('absolute h-full duration-200', props.disabled ? 'bg-ui-bevel' : 'bg-highlight-base')}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="absolute -top-2 -left-2 size-4 bg-transparent focus-visible:outline-hidden" />
        <span className="absolute top-1/2 right-2 -translate-y-1/2 text-sm font-medium text-black dark:text-white">
          {isLoading && (
            <Loader2 className="size-4 max-h-4 min-h-4 max-w-4 min-w-4 animate-spin opacity-50" strokeWidth={2.5} />
          )}
        </span>
      </SliderPrimitive.Root>
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        disabled={props.disabled}
        className="relative h-7 w-16 pr-5 text-center after:absolute after:top-1/2 after:right-2 after:-translate-y-1/2 after:content-['%']"
        aria-label={tLabels('qualityValue')}
      />
    </div>
  );
});

QualitySlider.displayName = 'QualitySlider';

export { QualitySlider };
